/**
 * Configuration schema using Zod
 */

import { z } from "zod";
import { sortFieldSchema, sortOrderSchema } from "../features/pagination/types.js";

/**
 * Log level schema
 */
export const logLevelSchema = z.enum(["silent", "info", "debug"]);

/**
 * TUI (interactive) search configuration schema
 */
export const tuiConfigSchema = z.object({
  limit: z.number().int().nonnegative(),
  debounceMs: z.number().int().nonnegative(),
  clipboardAutoCopy: z.boolean(),
});

/**
 * Edit format schema
 */
export const editFormatSchema = z.enum(["yaml", "json"]);

/**
 * Edit command configuration schema
 */
export const editConfigSchema = z.object({
  defaultFormat: editFormatSchema,
});

/**
 * CLI configuration schema
 */
export const cliConfigSchema = z.object({
  defaultLimit: z.number().int().nonnegative(),
  defaultSort: sortFieldSchema,
  defaultOrder: sortOrderSchema,
  tui: tuiConfigSchema,
  edit: editConfigSchema,
});

/**
 * MCP configuration schema
 */
export const mcpConfigSchema = z.object({
  defaultLimit: z.number().int().nonnegative(),
});

/**
 * Backup configuration schema
 */
export const backupConfigSchema = z.object({
  maxGenerations: z.number().int().positive(),
  maxAgeDays: z.number().int().positive(),
  directory: z.string().min(1),
});

/**
 * File watching configuration schema
 * Note: File watching is always enabled in server mode (HTTP/MCP).
 * CLI mode does not use file watching.
 */
export const watchConfigSchema = z.object({
  debounceMs: z.number().int().nonnegative(),
  pollIntervalMs: z.number().int().positive(),
  retryIntervalMs: z.number().int().positive(),
  maxRetries: z.number().int().nonnegative(),
  /**
   * Replace native file-system events with polling (fs.watchFile).
   * Native events plus a stat fallback are the default; turn this on only for
   * mounts where native watching fails outright.
   */
  usePolling: z.boolean(),
});

/**
 * Server configuration schema
 */
export const serverConfigSchema = z.object({
  autoStart: z.boolean(),
  autoStopMinutes: z.number().int().nonnegative(),
});

/**
 * Citation format schema
 */
export const citationFormatSchema = z.enum(["text", "html", "rtf"]);

/**
 * Citation key format schema
 */
export const citationKeyFormatSchema = z.enum(["pandoc", "latex"]);

/**
 * Citation configuration schema
 */
export const citationConfigSchema = z.object({
  defaultStyle: z.string(),
  cslDirectory: z.array(z.string()),
  defaultLocale: z.string(),
  defaultFormat: citationFormatSchema,
  defaultKeyFormat: citationKeyFormatSchema,
});

/**
 * PubMed API configuration schema
 */
export const pubmedConfigSchema = z.object({
  email: z.string().optional(),
  apiKey: z.string().optional(),
});

/**
 * Fulltext source enum schema
 */
export const fulltextSourceSchema = z.enum(["pmc", "arxiv", "unpaywall", "core"]);
const fulltextPreferredTypeSchema = z.enum(["pdf", "markdown"]);

/**
 * Fulltext retrieval configuration schema
 */
/**
 * Custom converter configuration schema
 */
export const customConverterSchema = z.object({
  command: z.string(),
  outputMode: z.enum(["file", "stdout"]).optional(),
  checkCommand: z.string().optional(),
  timeout: z.number().int().positive().optional(),
  progress: z.enum(["inherit", "quiet"]).optional(),
  commandWindows: z.string().optional(),
  checkCommandWindows: z.string().optional(),
});

/** Accepts both camelCase and snake_case keys for TOML compatibility */
const partialCustomConverterSchema = z.object({
  command: z.string().optional(),
  outputMode: z.enum(["file", "stdout"]).optional(),
  output_mode: z.enum(["file", "stdout"]).optional(),
  checkCommand: z.string().optional(),
  check_command: z.string().optional(),
  timeout: z.number().int().positive().optional(),
  progress: z.enum(["inherit", "quiet"]).optional(),
  commandWindows: z.string().optional(),
  command_windows: z.string().optional(),
  checkCommandWindows: z.string().optional(),
  check_command_windows: z.string().optional(),
});

export const fulltextConfigSchema = z.object({
  preferSources: z.array(fulltextSourceSchema),
  preferredType: fulltextPreferredTypeSchema.optional(),
  autoFetchOnAdd: z.boolean(),
  pdfConverter: z.string(),
  pdfConverterPriority: z.array(z.string()),
  pdfConverterTimeout: z.number().int().positive(),
  converters: z.record(z.string(), customConverterSchema),
  sources: z.object({
    unpaywallEmail: z.string().optional(),
    coreApiKey: z.string().optional(),
    ncbiEmail: z.string().optional(),
    ncbiTool: z.string().optional(),
  }),
});

/**
 * URL import archive format schema
 */
export const urlArchiveFormatSchema = z.enum(["mhtml", "html"]);

/**
 * URL import configuration schema
 */
export const urlConfigSchema = z.object({
  archiveFormat: urlArchiveFormatSchema,
  browserPath: z.string(),
  timeout: z.number().int().positive(),
});

/**
 * Attachments storage configuration schema
 */
export const attachmentsConfigSchema = z.object({
  directory: z.string().min(1),
});

/**
 * Complete configuration schema
 */
export const configSchema = z.object({
  library: z.string().min(1),
  logLevel: logLevelSchema,
  email: z.string().optional(),
  backup: backupConfigSchema,
  watch: watchConfigSchema,
  server: serverConfigSchema,
  citation: citationConfigSchema,
  pubmed: pubmedConfigSchema,
  fulltext: fulltextConfigSchema,
  attachments: attachmentsConfigSchema,
  url: urlConfigSchema,
  cli: cliConfigSchema,
  mcp: mcpConfigSchema,
});

/**
 * Partial configuration schema (for TOML files)
 * Supports both camelCase and snake_case field names
 */
export const partialConfigSchema = z
  .object({
    library: z.string().min(1).optional(),
    logLevel: logLevelSchema.optional(),
    log_level: logLevelSchema.optional(), // snake_case support
    email: z.string().optional(),
    backup: z
      .object({
        maxGenerations: z.number().int().positive().optional(),
        max_generations: z.number().int().positive().optional(),
        maxAgeDays: z.number().int().positive().optional(),
        max_age_days: z.number().int().positive().optional(),
        directory: z.string().min(1).optional(),
      })
      .optional(),
    watch: z
      .object({
        debounceMs: z.number().int().nonnegative().optional(),
        debounce_ms: z.number().int().nonnegative().optional(),
        pollIntervalMs: z.number().int().positive().optional(),
        poll_interval_ms: z.number().int().positive().optional(),
        retryIntervalMs: z.number().int().positive().optional(),
        retry_interval_ms: z.number().int().positive().optional(),
        maxRetries: z.number().int().nonnegative().optional(),
        max_retries: z.number().int().nonnegative().optional(),
        usePolling: z.boolean().optional(),
        use_polling: z.boolean().optional(),
      })
      .optional(),
    server: z
      .object({
        autoStart: z.boolean().optional(),
        auto_start: z.boolean().optional(),
        autoStopMinutes: z.number().int().nonnegative().optional(),
        auto_stop_minutes: z.number().int().nonnegative().optional(),
      })
      .optional(),
    citation: z
      .object({
        defaultStyle: z.string().optional(),
        default_style: z.string().optional(),
        cslDirectory: z.union([z.string(), z.array(z.string())]).optional(),
        csl_directory: z.union([z.string(), z.array(z.string())]).optional(),
        defaultLocale: z.string().optional(),
        default_locale: z.string().optional(),
        defaultFormat: citationFormatSchema.optional(),
        default_format: citationFormatSchema.optional(),
        defaultKeyFormat: citationKeyFormatSchema.optional(),
        default_key_format: citationKeyFormatSchema.optional(),
      })
      .optional(),
    pubmed: z
      .object({
        email: z.string().optional(),
        apiKey: z.string().optional(),
        api_key: z.string().optional(),
      })
      .optional(),
    fulltext: z
      .object({
        preferSources: z.array(fulltextSourceSchema).optional(),
        prefer_sources: z.array(fulltextSourceSchema).optional(),
        preferredType: fulltextPreferredTypeSchema.optional(),
        preferred_type: fulltextPreferredTypeSchema.optional(),
        autoFetchOnAdd: z.boolean().optional(),
        auto_fetch_on_add: z.boolean().optional(),
        pdfConverter: z.string().optional(),
        pdf_converter: z.string().optional(),
        pdfConverterPriority: z.array(z.string()).optional(),
        pdf_converter_priority: z.array(z.string()).optional(),
        pdfConverterTimeout: z.number().int().positive().optional(),
        pdf_converter_timeout: z.number().int().positive().optional(),
        converters: z
          .preprocess(
            (val) => {
              // @iarna/toml attaches Symbol metadata to tables; strip them for Zod
              if (val && typeof val === "object" && !Array.isArray(val)) {
                const clean: Record<string, unknown> = {};
                for (const [k, v] of Object.entries(val as Record<string, unknown>)) {
                  clean[k] = v;
                }
                return clean;
              }
              return val;
            },
            z.record(z.string(), partialCustomConverterSchema)
          )
          .optional(),
        sources: z
          .object({
            unpaywallEmail: z.string().optional(),
            unpaywall_email: z.string().optional(),
            coreApiKey: z.string().optional(),
            core_api_key: z.string().optional(),
            ncbiEmail: z.string().optional(),
            ncbi_email: z.string().optional(),
            ncbiTool: z.string().optional(),
            ncbi_tool: z.string().optional(),
          })
          .optional(),
      })
      .optional(),
    attachments: z
      .object({
        directory: z.string().min(1).optional(),
      })
      .optional(),
    url: z
      .object({
        archiveFormat: urlArchiveFormatSchema.optional(),
        archive_format: urlArchiveFormatSchema.optional(),
        browserPath: z.string().optional(),
        browser_path: z.string().optional(),
        timeout: z.number().int().positive().optional(),
      })
      .optional(),
    cli: z
      .object({
        defaultLimit: z.number().int().nonnegative().optional(),
        default_limit: z.number().int().nonnegative().optional(),
        defaultSort: sortFieldSchema.optional(),
        default_sort: sortFieldSchema.optional(),
        defaultOrder: sortOrderSchema.optional(),
        default_order: sortOrderSchema.optional(),
        tui: z
          .object({
            limit: z.number().int().nonnegative().optional(),
            debounceMs: z.number().int().nonnegative().optional(),
            debounce_ms: z.number().int().nonnegative().optional(),
            clipboardAutoCopy: z.boolean().optional(),
            clipboard_auto_copy: z.boolean().optional(),
          })
          .optional(),
        edit: z
          .object({
            defaultFormat: editFormatSchema.optional(),
            default_format: editFormatSchema.optional(),
          })
          .optional(),
      })
      .optional(),
    mcp: z
      .object({
        defaultLimit: z.number().int().nonnegative().optional(),
        default_limit: z.number().int().nonnegative().optional(),
      })
      .optional(),
  })
  .passthrough(); // Allow unknown fields in TOML files

/**
 * Inferred types from schemas
 */
export type LogLevel = z.infer<typeof logLevelSchema>;
export type BackupConfig = z.infer<typeof backupConfigSchema>;
export type WatchConfig = z.infer<typeof watchConfigSchema>;
export type ServerConfig = z.infer<typeof serverConfigSchema>;
export type CitationFormat = z.infer<typeof citationFormatSchema>;
export type CitationKeyFormat = z.infer<typeof citationKeyFormatSchema>;
export type CitationConfig = z.infer<typeof citationConfigSchema>;
export type PubmedConfig = z.infer<typeof pubmedConfigSchema>;
export type FulltextSource = z.infer<typeof fulltextSourceSchema>;
export type FulltextPreferredType = z.infer<typeof fulltextPreferredTypeSchema>;
export type FulltextConfig = z.infer<typeof fulltextConfigSchema>;
export type AttachmentsConfig = z.infer<typeof attachmentsConfigSchema>;
export type UrlArchiveFormat = z.infer<typeof urlArchiveFormatSchema>;
export type UrlConfig = z.infer<typeof urlConfigSchema>;
export type TuiConfig = z.infer<typeof tuiConfigSchema>;
export type EditConfigFormat = z.infer<typeof editFormatSchema>;
export type EditConfig = z.infer<typeof editConfigSchema>;
export type CliConfig = z.infer<typeof cliConfigSchema>;
export type McpConfig = z.infer<typeof mcpConfigSchema>;
export type Config = z.infer<typeof configSchema>;
export type PartialConfig = z.infer<typeof partialConfigSchema>;

/**
 * Deep partial type for Config
 */
export type DeepPartialConfig = {
  library?: string;
  logLevel?: LogLevel;
  email?: string;
  backup?: Partial<BackupConfig>;
  watch?: Partial<WatchConfig>;
  server?: Partial<ServerConfig>;
  citation?: Partial<CitationConfig>;
  pubmed?: Partial<PubmedConfig>;
  fulltext?: Partial<Omit<FulltextConfig, "sources" | "converters">> & {
    sources?: Partial<FulltextConfig["sources"]>;
    converters?: FulltextConfig["converters"];
  };
  attachments?: Partial<AttachmentsConfig>;
  url?: Partial<UrlConfig>;
  cli?: Partial<Omit<CliConfig, "tui" | "edit">> & {
    tui?: Partial<TuiConfig>;
    edit?: Partial<EditConfig>;
  };
  mcp?: Partial<McpConfig>;
};

/**
 * Normalize backup configuration from snake_case to camelCase
 */
function normalizeBackupConfig(
  backup: Partial<{
    maxGenerations?: number;
    max_generations?: number;
    maxAgeDays?: number;
    max_age_days?: number;
    directory?: string;
  }>
): Partial<BackupConfig> | undefined {
  const normalized: Partial<BackupConfig> = {};

  const maxGenerations = backup.maxGenerations ?? backup.max_generations;
  if (maxGenerations !== undefined) {
    normalized.maxGenerations = maxGenerations;
  }

  const maxAgeDays = backup.maxAgeDays ?? backup.max_age_days;
  if (maxAgeDays !== undefined) {
    normalized.maxAgeDays = maxAgeDays;
  }

  if (backup.directory !== undefined) {
    normalized.directory = backup.directory;
  }

  return Object.keys(normalized).length > 0 ? normalized : undefined;
}

/**
 * Normalize watch configuration from snake_case to camelCase
 */
function normalizeWatchConfig(
  watch: Partial<{
    debounceMs?: number;
    debounce_ms?: number;
    pollIntervalMs?: number;
    poll_interval_ms?: number;
    retryIntervalMs?: number;
    retry_interval_ms?: number;
    maxRetries?: number;
    max_retries?: number;
    usePolling?: boolean;
    use_polling?: boolean;
  }>
): Partial<WatchConfig> | undefined {
  const normalized: Partial<WatchConfig> = {};

  const debounceMs = watch.debounceMs ?? watch.debounce_ms;
  if (debounceMs !== undefined) {
    normalized.debounceMs = debounceMs;
  }

  const pollIntervalMs = watch.pollIntervalMs ?? watch.poll_interval_ms;
  if (pollIntervalMs !== undefined) {
    normalized.pollIntervalMs = pollIntervalMs;
  }

  const retryIntervalMs = watch.retryIntervalMs ?? watch.retry_interval_ms;
  if (retryIntervalMs !== undefined) {
    normalized.retryIntervalMs = retryIntervalMs;
  }

  const maxRetries = watch.maxRetries ?? watch.max_retries;
  if (maxRetries !== undefined) {
    normalized.maxRetries = maxRetries;
  }

  const usePolling = watch.usePolling ?? watch.use_polling;
  if (usePolling !== undefined) {
    normalized.usePolling = usePolling;
  }

  return Object.keys(normalized).length > 0 ? normalized : undefined;
}

/**
 * Normalize server configuration from snake_case to camelCase
 */
function normalizeServerConfig(
  server: Partial<{
    autoStart?: boolean;
    auto_start?: boolean;
    autoStopMinutes?: number;
    auto_stop_minutes?: number;
  }>
): Partial<ServerConfig> | undefined {
  const normalized: Partial<ServerConfig> = {};

  const autoStart = server.autoStart ?? server.auto_start;
  if (autoStart !== undefined) {
    normalized.autoStart = autoStart;
  }

  const autoStopMinutes = server.autoStopMinutes ?? server.auto_stop_minutes;
  if (autoStopMinutes !== undefined) {
    normalized.autoStopMinutes = autoStopMinutes;
  }

  return Object.keys(normalized).length > 0 ? normalized : undefined;
}

/**
 * Normalize citation configuration from snake_case to camelCase
 */
function normalizeCitationConfig(
  citation: Partial<{
    defaultStyle?: string;
    default_style?: string;
    cslDirectory?: string | string[];
    csl_directory?: string | string[];
    defaultLocale?: string;
    default_locale?: string;
    defaultFormat?: CitationFormat;
    default_format?: CitationFormat;
    defaultKeyFormat?: CitationKeyFormat;
    default_key_format?: CitationKeyFormat;
  }>
): Partial<CitationConfig> | undefined {
  const normalized: Partial<CitationConfig> = {};

  const defaultStyle = citation.defaultStyle ?? citation.default_style;
  if (defaultStyle !== undefined) {
    normalized.defaultStyle = defaultStyle;
  }

  const cslDirectory = citation.cslDirectory ?? citation.csl_directory;
  if (cslDirectory !== undefined) {
    // Normalize to array: string -> [string]
    normalized.cslDirectory = Array.isArray(cslDirectory) ? cslDirectory : [cslDirectory];
  }

  const defaultLocale = citation.defaultLocale ?? citation.default_locale;
  if (defaultLocale !== undefined) {
    normalized.defaultLocale = defaultLocale;
  }

  const defaultFormat = citation.defaultFormat ?? citation.default_format;
  if (defaultFormat !== undefined) {
    normalized.defaultFormat = defaultFormat;
  }

  const defaultKeyFormat =
    (citation as Record<string, unknown>).defaultKeyFormat ??
    (citation as Record<string, unknown>).default_key_format;
  if (defaultKeyFormat !== undefined) {
    normalized.defaultKeyFormat = defaultKeyFormat as CitationKeyFormat;
  }

  return Object.keys(normalized).length > 0 ? normalized : undefined;
}

/**
 * Normalize pubmed configuration from snake_case to camelCase
 */
function normalizePubmedConfig(
  pubmed: Partial<{
    email?: string;
    apiKey?: string;
    api_key?: string;
  }>
): Partial<PubmedConfig> | undefined {
  const normalized: Partial<PubmedConfig> = {};

  if (pubmed.email !== undefined) {
    normalized.email = pubmed.email;
  }

  const apiKey = pubmed.apiKey ?? pubmed.api_key;
  if (apiKey !== undefined) {
    normalized.apiKey = apiKey;
  }

  return Object.keys(normalized).length > 0 ? normalized : undefined;
}

/**
 * Normalize fulltext configuration from snake_case to camelCase
 */
function normalizeFulltextConfig(
  fulltext: Partial<{
    preferSources?: FulltextSource[];
    prefer_sources?: FulltextSource[];
    preferredType?: FulltextPreferredType;
    preferred_type?: FulltextPreferredType;
    autoFetchOnAdd?: boolean;
    auto_fetch_on_add?: boolean;
    pdfConverter?: string;
    pdf_converter?: string;
    pdfConverterPriority?: string[];
    pdf_converter_priority?: string[];
    pdfConverterTimeout?: number;
    pdf_converter_timeout?: number;
    converters?: FulltextConfig["converters"];
    sources?: Partial<{
      unpaywallEmail?: string;
      unpaywall_email?: string;
      coreApiKey?: string;
      core_api_key?: string;
    }>;
  }>
): DeepPartialConfig["fulltext"] | undefined {
  const normalized: NonNullable<DeepPartialConfig["fulltext"]> = {};

  const preferSources = fulltext.preferSources ?? fulltext.prefer_sources;
  if (preferSources !== undefined) {
    normalized.preferSources = preferSources;
  }

  const preferredType = fulltext.preferredType ?? fulltext.preferred_type;
  if (preferredType !== undefined) {
    normalized.preferredType = preferredType;
  }

  const autoFetchOnAdd = fulltext.autoFetchOnAdd ?? fulltext.auto_fetch_on_add;
  if (autoFetchOnAdd !== undefined) {
    normalized.autoFetchOnAdd = autoFetchOnAdd;
  }

  const pdfConverter = fulltext.pdfConverter ?? fulltext.pdf_converter;
  if (pdfConverter !== undefined) {
    normalized.pdfConverter = pdfConverter;
  }

  const pdfConverterPriority = fulltext.pdfConverterPriority ?? fulltext.pdf_converter_priority;
  if (pdfConverterPriority !== undefined) {
    normalized.pdfConverterPriority = pdfConverterPriority;
  }

  const pdfConverterTimeout = fulltext.pdfConverterTimeout ?? fulltext.pdf_converter_timeout;
  if (pdfConverterTimeout !== undefined) {
    normalized.pdfConverterTimeout = pdfConverterTimeout;
  }

  if (fulltext.converters !== undefined) {
    normalized.converters = normalizeConverters(fulltext.converters);
  }

  const sources = normalizeFulltextSources(fulltext.sources);
  if (sources !== undefined) {
    normalized.sources = sources;
  }

  return Object.keys(normalized).length > 0 ? normalized : undefined;
}

/** camelCase key -> snake_case alias for converter config fields */
const CONVERTER_KEY_ALIASES: [string, string][] = [
  ["outputMode", "output_mode"],
  ["checkCommand", "check_command"],
  ["commandWindows", "command_windows"],
  ["checkCommandWindows", "check_command_windows"],
];

function normalizeConverterEntry(
  conv: Record<string, unknown>
): FulltextConfig["converters"][string] {
  const entry: Record<string, unknown> = {};
  // Copy fields that have no alias
  for (const key of ["command", "timeout", "progress"]) {
    if (conv[key] !== undefined) entry[key] = conv[key];
  }
  // Normalize aliased fields (camelCase preferred, snake_case fallback)
  for (const [camel, snake] of CONVERTER_KEY_ALIASES) {
    const val = conv[camel] ?? conv[snake];
    if (val !== undefined) entry[camel] = val;
  }
  return entry as FulltextConfig["converters"][string];
}

function normalizeConverters(
  converters: Record<string, Record<string, unknown>>
): FulltextConfig["converters"] {
  const result: FulltextConfig["converters"] = {};
  for (const [name, conv] of Object.entries(converters)) {
    result[name] = normalizeConverterEntry(conv);
  }
  return result;
}

function normalizeFulltextSources(
  sources?: Partial<{
    unpaywallEmail?: string;
    unpaywall_email?: string;
    coreApiKey?: string;
    core_api_key?: string;
    ncbiEmail?: string;
    ncbi_email?: string;
    ncbiTool?: string;
    ncbi_tool?: string;
  }>
): Partial<FulltextConfig["sources"]> | undefined {
  if (sources === undefined) {
    return undefined;
  }

  const normalized: Partial<FulltextConfig["sources"]> = {};

  const unpaywallEmail = sources.unpaywallEmail ?? sources.unpaywall_email;
  if (unpaywallEmail !== undefined) {
    normalized.unpaywallEmail = unpaywallEmail;
  }

  const coreApiKey = sources.coreApiKey ?? sources.core_api_key;
  if (coreApiKey !== undefined) {
    normalized.coreApiKey = coreApiKey;
  }

  const ncbiEmail = sources.ncbiEmail ?? sources.ncbi_email;
  if (ncbiEmail !== undefined) {
    normalized.ncbiEmail = ncbiEmail;
  }

  const ncbiTool = sources.ncbiTool ?? sources.ncbi_tool;
  if (ncbiTool !== undefined) {
    normalized.ncbiTool = ncbiTool;
  }

  return Object.keys(normalized).length > 0 ? normalized : undefined;
}

/**
 * Normalize URL configuration from snake_case to camelCase
 */
function normalizeUrlConfig(
  url: Partial<{
    archiveFormat?: UrlArchiveFormat;
    archive_format?: UrlArchiveFormat;
    browserPath?: string;
    browser_path?: string;
    timeout?: number;
  }>
): Partial<UrlConfig> | undefined {
  const normalized: Partial<UrlConfig> = {};

  const archiveFormat = url.archiveFormat ?? url.archive_format;
  if (archiveFormat !== undefined) {
    normalized.archiveFormat = archiveFormat;
  }

  const browserPath = url.browserPath ?? url.browser_path;
  if (browserPath !== undefined) {
    normalized.browserPath = browserPath;
  }

  if (url.timeout !== undefined) {
    normalized.timeout = url.timeout;
  }

  return Object.keys(normalized).length > 0 ? normalized : undefined;
}

/**
 * Section normalizers mapping
 */
const sectionNormalizers = {
  backup: normalizeBackupConfig,
  watch: normalizeWatchConfig,
  server: normalizeServerConfig,
  citation: normalizeCitationConfig,
  pubmed: normalizePubmedConfig,
  fulltext: normalizeFulltextConfig,
  attachments: normalizeAttachmentsConfig,
  url: normalizeUrlConfig,
  cli: normalizeCliConfig,
  mcp: normalizeMcpConfig,
} as const;

type SectionKey = keyof typeof sectionNormalizers;

/**
 * Helper to apply a normalizer function to a config section
 */
function applyNormalizer<K extends SectionKey>(
  normalized: DeepPartialConfig,
  partial: PartialConfig,
  key: K,
  normalizer: (typeof sectionNormalizers)[K]
): void {
  const value = partial[key];
  if (value !== undefined) {
    const result = (normalizer as (input: unknown) => DeepPartialConfig[K] | undefined)(value);
    if (result) {
      normalized[key] = result;
    }
  }
}

/**
 * Normalize snake_case fields to camelCase
 */
export function normalizePartialConfig(partial: PartialConfig): DeepPartialConfig {
  const normalized: DeepPartialConfig = {};

  // Simple fields
  if (partial.library !== undefined) {
    normalized.library = partial.library;
  }
  const logLevel = partial.logLevel ?? partial.log_level;
  if (logLevel !== undefined) {
    normalized.logLevel = logLevel;
  }
  if (partial.email !== undefined) {
    normalized.email = partial.email;
  }

  // Section fields
  for (const key of Object.keys(sectionNormalizers) as SectionKey[]) {
    applyNormalizer(normalized, partial, key, sectionNormalizers[key]);
  }

  return normalized;
}

/**
 * Normalize attachments configuration
 */
function normalizeAttachmentsConfig(attachments: {
  directory?: string | undefined;
}): Partial<AttachmentsConfig> | undefined {
  const normalized: Partial<AttachmentsConfig> = {};

  if (attachments.directory !== undefined) {
    normalized.directory = attachments.directory;
  }

  return Object.keys(normalized).length > 0 ? normalized : undefined;
}

/**
 * Normalize TUI config subsection
 */
function normalizeTuiSection(
  tui: Partial<{
    limit?: number;
    debounceMs?: number;
    debounce_ms?: number;
    clipboardAutoCopy?: boolean;
    clipboard_auto_copy?: boolean;
  }>
): Partial<TuiConfig> | undefined {
  const normalized: Partial<TuiConfig> = {};
  if (tui.limit !== undefined) {
    normalized.limit = tui.limit;
  }
  const debounceMs = tui.debounceMs ?? tui.debounce_ms;
  if (debounceMs !== undefined) {
    normalized.debounceMs = debounceMs;
  }
  const clipboardAutoCopy = tui.clipboardAutoCopy ?? tui.clipboard_auto_copy;
  if (clipboardAutoCopy !== undefined) {
    normalized.clipboardAutoCopy = clipboardAutoCopy;
  }
  return Object.keys(normalized).length > 0 ? normalized : undefined;
}

/**
 * Normalize edit config subsection
 */
function normalizeEditSection(
  edit: Partial<{
    defaultFormat?: EditConfigFormat;
    default_format?: EditConfigFormat;
  }>
): Partial<EditConfig> | undefined {
  const normalized: Partial<EditConfig> = {};
  const defaultFormat = edit.defaultFormat ?? edit.default_format;
  if (defaultFormat !== undefined) {
    normalized.defaultFormat = defaultFormat;
  }
  return Object.keys(normalized).length > 0 ? normalized : undefined;
}

/**
 * Normalize CLI configuration from snake_case to camelCase
 */
function normalizeCliConfig(
  cli: Partial<{
    defaultLimit?: number;
    default_limit?: number;
    defaultSort?: CliConfig["defaultSort"];
    default_sort?: CliConfig["defaultSort"];
    defaultOrder?: CliConfig["defaultOrder"];
    default_order?: CliConfig["defaultOrder"];
    tui?: Partial<{
      limit?: number;
      debounceMs?: number;
      debounce_ms?: number;
    }>;
    edit?: Partial<{
      defaultFormat?: EditConfigFormat;
      default_format?: EditConfigFormat;
    }>;
  }>
): Partial<CliConfig> | undefined {
  const normalized: Partial<CliConfig> = {};

  const defaultLimit = cli.defaultLimit ?? cli.default_limit;
  if (defaultLimit !== undefined) {
    normalized.defaultLimit = defaultLimit;
  }

  const defaultSort = cli.defaultSort ?? cli.default_sort;
  if (defaultSort !== undefined) {
    normalized.defaultSort = defaultSort;
  }

  const defaultOrder = cli.defaultOrder ?? cli.default_order;
  if (defaultOrder !== undefined) {
    normalized.defaultOrder = defaultOrder;
  }

  if (cli.tui !== undefined) {
    const tui = normalizeTuiSection(cli.tui);
    if (tui) {
      normalized.tui = tui as TuiConfig;
    }
  }

  if (cli.edit !== undefined) {
    const edit = normalizeEditSection(cli.edit);
    if (edit) {
      normalized.edit = edit as EditConfig;
    }
  }

  return Object.keys(normalized).length > 0 ? normalized : undefined;
}

/**
 * Normalize MCP configuration from snake_case to camelCase
 */
function normalizeMcpConfig(
  mcp: Partial<{
    defaultLimit?: number;
    default_limit?: number;
  }>
): Partial<McpConfig> | undefined {
  const normalized: Partial<McpConfig> = {};

  const defaultLimit = mcp.defaultLimit ?? mcp.default_limit;
  if (defaultLimit !== undefined) {
    normalized.defaultLimit = defaultLimit;
  }

  return Object.keys(normalized).length > 0 ? normalized : undefined;
}
